import _ from 'lodash';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Tag } from 'src/@vymo/ui/atoms';
import Button from 'src/@vymo/ui/atoms/button';
import Input from 'src/@vymo/ui/atoms/input';
import OptionsList from 'src/@vymo/ui/atoms/select/options';
import {
  getFlattenOptions,
  getOpenNodeIds,
  getSearchResult,
} from 'src/@vymo/ui/atoms/select/queries';
import type { Option } from 'src/@vymo/ui/atoms/select/types';
import { ReactComponent as CaretDownRound } from 'src/assets/icons/caretDown.svg';
import { ReactComponent as CaretRightRound } from 'src/assets/icons/caretRight.svg';
import { ReactComponent as CollapsibleIcon } from 'src/assets/icons/collapseFiles.svg';
import { ReactComponent as SearchIcon } from 'src/assets/icons/search.svg';
import { TreeListProps } from './types';
import styles from './index.module.scss';

function TreeList({
  value,
  options = [],
  expandedNodesLevel = 0,
  showSearch = true,
  showCollapse = true,
  icon,
  'data-test-id': dataTestId,
  multi = false,
  onChange = () => {},
  showChips = false,
  onChipClose = () => {},
}: TreeListProps) {
  const {
    collapse = <CaretRightRound />,
    expand = <CaretDownRound />,
    position = 'left',
  } = icon ?? {};
  // showSearch functionality needs to be implemnted
  const [treeValue, setTreeValue] =
    useState<Array<{ code: string; name: string }>>(value);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setTreeValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const isSelected = useCallback(
    (option: Option) =>
      treeValue.some(
        (item) => item.code === String(option.code ?? option.value),
      ),
    [treeValue],
  );

  const optionsListRef = useRef<HTMLDivElement>(null);

  const onCheckboxClick = (
    isChecked: boolean,
    option: Option,
    event: SyntheticEvent,
  ) => {
    const optionId = option.code ?? option.value;
    const optionName = option.name ?? option.label;
    let updatedValues;

    if (multi) {
      if (isChecked) {
        updatedValues = [...treeValue, { code: optionId, name: optionName }];
      } else {
        updatedValues = treeValue.filter((item) => item.code !== optionId);
      }
    } else {
      updatedValues = [{ code: optionId, name: optionName }];
    }

    setTreeValue(updatedValues);
    onChange(updatedValues, event);
  };

  const onSearch = useCallback((searchInputValue) => {
    if (showSearch) {
      setSearchValue(searchInputValue);
      const filterResult = getSearchResult(
        getFlattenOptions(options),
        searchInputValue,
        false,
      );
      const openNodeIds = getOpenNodeIds(filterResult);
      // @ts-ignore
      optionsListRef?.current?.setSearchNodesId?.(openNodeIds);
      // @ts-ignore
      optionsListRef?.current?.setOpenNodesId?.(openNodeIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collapseAll = () => {
    // @ts-ignore
    optionsListRef?.current?.setOpenNodesId?.([]);
  };

  const onSearchInputChange = () => {};
  return (
    <div className={styles.treeList}>
      {(showSearch || showCollapse) && (
        <div className={styles.treeList__header}>
          {showSearch && (
            <Input
              classNames={styles.treeList__header__search}
              value={searchValue}
              onChange={onSearch}
              iconRight={<SearchIcon />}
            />
          )}
          {showCollapse && (
            <Button
              type="text"
              variant="filled"
              onClick={collapseAll}
              isIcon
              iconProps={{ icon: <CollapsibleIcon />, iconPosition: 'left' }}
            />
          )}
        </div>
      )}

      <div className={styles.treeList__chipContainer}>
        {showChips && (
          <div className={styles.treeList__chipContainer__chip}>
            {treeValue?.map((item) => (
              <Tag
                type="primary"
                className={styles.treeList__chipContainer__chipTag}
                label={item.name}
                showClose
                onClose={() => {
                  onChipClose(item);
                }}
              />
            ))}
          </div>
        )}

        <OptionsList
          data-test-id={`treeList-${dataTestId}`}
          ref={optionsListRef}
          isSelected={isSelected}
          options={options}
          onItemClick={_.noop}
          expandedNodesLevel={expandedNodesLevel}
          isSearch={searchValue.length > 0}
          isMulti={Boolean(multi)}
          onSearchInputChange={onSearchInputChange}
          onCheckboxClick={onCheckboxClick}
          mode="tree"
          icon={{ collapse, expand, position }}
        />
      </div>
    </div>
  );
}

export default TreeList;
